// Chat file attachments: validate against the agent_chat file_upload limits, upload to S3 via the
// server endpoint, and dispatch per mode.
//   • text / push-to-talk → files are STAGED (chips) and sent with the turn (images as vision,
//     non-images extracted inline + ingested to the session KB by the backend).
//   • interactive (realtime) → uploaded immediately, then per type: images go to the model as
//     vision input via `agent_realtime.image` (S3 URL, not base64 — avoids socket-size limits);
//     non-images notify via `agent_realtime.file` so the backend ingests them to the KB and tells
//     the agent to search_file_attachment_store.
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { socketService } from '@/services/SocketService';
import {
  useUploadChatFilesMutation,
  type FileUploadConfig,
  type UploadedFile,
} from '@/redux/internal-services/agentApi';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];

const isImageUpload = (f: UploadedFile): boolean =>
  (f.type || '').startsWith('image/') || IMAGE_EXTS.some((e) => f.name.toLowerCase().endsWith(`.${e}`));

/**
 * Downscale + JPEG-encode an image to a base64 data URL. The OpenAI Realtime API rejects remote
 * image URLs for `input_image` — it needs the bytes inline — so realtime images go as a compact
 * data URL (a remote S3 URL is still used for the chat bubble + persistence). Keeps it well under
 * the Socket.IO 1MB frame limit.
 */
function compressImageToDataUrl(file: File, maxDim = 1024, quality = 0.85): Promise<string> {
  return new Promise((resolve, reject) => {
    const objUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      URL.revokeObjectURL(objUrl);
      if (!ctx) return reject(new Error('no canvas 2d context'));
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = (e) => { URL.revokeObjectURL(objUrl); reject(e); };
    img.src = objUrl;
  });
}

export interface ChatAttachments {
  attachments: UploadedFile[];
  uploading: boolean;
  error: string | null;
  /** Pick files from the composer. Interactive mode dispatches immediately; else stages chips. */
  addFiles: (files: FileList | File[], caption?: string) => Promise<void>;
  removeAttachment: (url: string) => void;
  clear: () => void;
  setError: (e: string | null) => void;
}

export function useChatAttachments(opts: {
  cfg?: FileUploadConfig;
  isInteractive: boolean;
  conversationId: string;
  /** In interactive mode files dispatch immediately (no chips), so echo them as a user bubble. */
  onLocalEcho?: (files: UploadedFile[]) => void;
}): ChatAttachments {
  const { t } = useTranslation();
  const { cfg, isInteractive, conversationId, onLocalEcho } = opts;
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadFiles] = useUploadChatFilesMutation();

  const validate = useCallback(
    (files: File[]): { ok: File[]; err?: string } => {
      if (!cfg?.enabled) return { ok: [], err: t('common.fileUploadsDisabled') };
      const allowed = cfg.allowed_types || [];
      const ok: File[] = [];
      for (const f of files) {
        const ext = f.name.split('.').pop()?.toLowerCase() || '';
        if (allowed.length && !allowed.includes(ext)) return { ok, err: t('common.fileExtensionNotAllowed', { ext }) };
        if (f.size > cfg.max_size_mb * 1024 * 1024) return { ok, err: t('common.fileExceedsMaxSize', { name: f.name, maxSizeMb: cfg.max_size_mb }) };
        ok.push(f);
      }
      if (attachments.length + ok.length > cfg.max_files) return { ok: [], err: t('common.fileLimitPerMessage', { maxFiles: cfg.max_files }) };
      return { ok };
    },
    [cfg, attachments.length, t],
  );

  const addFiles = useCallback(
    async (fileList: FileList | File[], caption?: string) => {
      setError(null);
      const files = Array.from(fileList);
      if (!files.length) return;
      const { ok, err } = validate(files);
      if (err) setError(err);
      if (!ok.length) return;

      if (isInteractive) {
        // Upload everything, then route by type to the live voice session.
        setUploading(true);
        try {
          // uploadFiles preserves order, so uploaded[i] corresponds to ok[i].
          const uploaded = await uploadFiles(ok).unwrap();
          for (let i = 0; i < uploaded.length; i++) {
            const f = uploaded[i];
            if (isImageUpload(f)) {
              // Realtime needs the image bytes inline (data URL); the S3 url rides along for record.
              let dataUrl = '';
              try { dataUrl = await compressImageToDataUrl(ok[i]); } catch { dataUrl = ''; }
              socketService.emit('message', {
                type: 'agent_realtime.image',
                session_id: conversationId,
                image_url: dataUrl || f.url,
                url: f.url,
                name: f.name,
                caption: caption || '',
              });
            } else {
              socketService.emit('message', {
                type: 'agent_realtime.file',
                session_id: conversationId,
                url: f.url,
                name: f.name,
                file_type: f.type,
              });
            }
          }
          // Show the shared file(s) as a user bubble — interactive doesn't stage chips in the composer.
          if (uploaded.length) onLocalEcho?.(uploaded);
        } catch { setError(t('common.uploadFailed')); }
        finally { setUploading(false); }
        return;
      }

      // text / push-to-talk: upload and stage to send with the turn.
      setUploading(true);
      try {
        const uploaded = await uploadFiles(ok).unwrap();
        setAttachments((prev) => [...prev, ...uploaded]);
      } catch { setError(t('common.uploadFailed')); }
      finally { setUploading(false); }
    },
    [validate, isInteractive, conversationId, uploadFiles, onLocalEcho, t],
  );

  const removeAttachment = useCallback((url: string) => {
    setAttachments((prev) => prev.filter((a) => a.url !== url));
  }, []);
  const clear = useCallback(() => setAttachments([]), []);

  return { attachments, uploading, error, addFiles, removeAttachment, clear, setError };
}
